/*
  Add-to-bag offer step (cart drawer).

  Session state (sessionStorage "cgUpsellOffer"):
    recent    – variant ids added from product forms, newest last
    declined  – offer handles the customer said "No thanks" to
    animate   – true right after an add, so the step unfolds once
    highlight – offer variant id to flash after it was accepted
    accepted  – trigger variant id → accepted offer variant id

  Which offer shows:
    Walk `recent` from newest to oldest, skipping variants no longer in the
    cart and variants that aren't triggers. The first trigger found decides:
    show its offer unless declined / not rendered (already in bag or
    unavailable). Older triggers never act as a fallback, so an earlier
    unanswered offer is dropped as soon as a newer trigger is added.
    Closing the drawer is not an answer: the step shows again on next open.
*/

var WDN = WDN || {};

if (typeof WDN.UpsellOfferState === "undefined") {
  WDN.UpsellOfferState = (function () {
    var KEY = "cgUpsellOffer";
    var MAX_RECENT = 20;

    var read = function () {
      try {
        var state = JSON.parse(sessionStorage.getItem(KEY)) || {};
        state.recent = state.recent || [];
        state.declined = state.declined || [];
        return state;
      } catch (e) {
        return { recent: [], declined: [] };
      }
    };

    var write = function (state) {
      try {
        sessionStorage.setItem(KEY, JSON.stringify(state));
      } catch (e) {}
    };

    var update = function (fn) {
      var state = read();
      fn(state);
      write(state);
      return state;
    };

    // Product-form adds dispatch "cart:updated" with the cart/add.js response
    // (a single line item, or { items: [...] }) before the drawer re-renders.
    document.body.addEventListener("cart:updated", function (e) {
      var detail = e.detail || {};
      var items = detail.items || (detail.variant_id ? [detail] : []);
      var added = items.filter(function (item) {
        return !(item.properties && item.properties._upsell);
      });
      if (!added.length) return;

      update(function (state) {
        added.forEach(function (item) {
          var id = String(item.variant_id);
          state.recent = state.recent.filter(function (v) {
            return v !== id;
          });
          state.recent.push(id);
        });
        state.recent = state.recent.slice(-MAX_RECENT);
        state.animate = true;
      });
    });

    return { read: read, update: update };
  })();
}

if (!customElements.get("wdn-upsell-offer-step")) {
  class WDNUpsellOfferStep extends HTMLElement {
    pickOffer(state) {
      var triggers = (this.dataset.triggers || "").split(",");
      var inCart = (this.dataset.cartVariants || "").split(",");

      for (var i = state.recent.length - 1; i >= 0; i--) {
        var id = state.recent[i];
        if (inCart.indexOf(id) === -1) continue;
        if (triggers.indexOf(id) === -1) continue;

        var offer = this.querySelector(
          '.upsell-offer-step__offer[data-trigger="' + id + '"]'
        );
        if (offer) {
          if (state.declined.indexOf(offer.dataset.handle) !== -1) return null;
          return { offer: offer };
        }

        // No offer to show: if this kit's offer was accepted, keep the
        // kit + added item together on top.
        if (state.accepted && state.accepted[id]) {
          return { accepted: { trigger: id, offerVariant: state.accepted[id] } };
        }
        return null;
      }
      return null;
    }

    track(event, offer) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: event,
        upsell_offer: offer.dataset.handle,
        upsell_offer_variant: offer.dataset.offerVariant,
        upsell_trigger_variant: offer.dataset.trigger,
      });
    }

    collapse(callback) {
      this.classList.remove("is-open");
      setTimeout(callback || function () {}, 350);
    }

    accept(button) {
      var offer = this.activeOffer;
      button.setAttribute("disabled", "");
      this.track("upsell_offer_accepted", offer);

      fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: Number(offer.dataset.offerVariant),
              quantity: 1,
              properties: {
                _upsell: offer.dataset.handle,
                _upsell_source: "drawer",
              },
            },
          ],
        }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Add failed");
          return res.json();
        })
        .then(() => {
          WDN.UpsellOfferState.update(function (state) {
            state.highlight = offer.dataset.offerVariant;
            state.accepted = state.accepted || {};
            state.accepted[offer.dataset.trigger] = offer.dataset.offerVariant;
          });
          // Show the check briefly, then fold the card away and refresh.
          button.classList.add("is-added");
          setTimeout(() => {
            this.collapse(function () {
              WDN.CartDrawer.refresh();
            });
          }, 500);
        })
        .catch(() => {
          button.removeAttribute("disabled");
        });
    }

    decline() {
      var offer = this.activeOffer;
      this.track("upsell_offer_declined", offer);
      WDN.UpsellOfferState.update(function (state) {
        if (state.declined.indexOf(offer.dataset.handle) === -1) {
          state.declined.push(offer.dataset.handle);
        }
      });
      this.collapse();
    }

    // Show the product that triggered the offer right above it, in the same
    // compact layout. The cart line itself is moved (not copied), so its
    // quantity and remove controls keep working.
    moveAddedLine(offer) {
      var slot = offer.querySelector("[data-added-slot]");
      var line = this.findLine(offer.dataset.trigger, false);
      if (slot && line) this.moveToSlot(line, slot);
    }

    // Offer accepted: kit first, then the added offer item, both compact.
    showAccepted(accepted) {
      var slot = this.querySelector("[data-accepted-slot]");
      var kit = this.findLine(accepted.trigger, false);
      var added = this.findLine(accepted.offerVariant, true);
      if (!slot || !kit || !added) return;

      this.moveToSlot(kit, slot);
      this.moveToSlot(added, slot);
      slot.hidden = false;
    }

    findLine(variantId, isUpsell) {
      var drawer = this.closest("[data-cartdrawer-content]") || document;
      var grid = drawer.querySelector(".cart-items--drawer");
      if (!grid) return null;
      return grid.querySelector(
        '.cart__item[data-variant-id="' +
          variantId +
          '"]' +
          (isUpsell ? "[data-upsell-offer]" : ":not([data-upsell-offer])")
      );
    }

    moveToSlot(line, slot) {
      var grid = line.parentElement;
      line.classList.add("cart__item--compact");
      slot.appendChild(line);
      if (grid && !grid.querySelector(".cart__item")) {
        grid.hidden = true;
        this.setAttribute("data-list-empty", "");
      }
    }

    highlightAcceptedLine(state) {
      if (!state.highlight) return;
      var drawer = this.closest("[data-cartdrawer-content]") || document;
      var line = drawer.querySelector(
        '.cart__item[data-variant-id="' + state.highlight + '"]'
      );
      if (line) line.classList.add("cart__item--upsell-new");
      WDN.UpsellOfferState.update(function (s) {
        delete s.highlight;
      });
    }

    connectedCallback() {
      var state = WDN.UpsellOfferState.read();
      this.highlightAcceptedLine(state);

      var pick = this.pickOffer(state);
      if (pick && pick.accepted) {
        this.showAccepted(pick.accepted);
        return;
      }

      this.activeOffer = pick && pick.offer;
      if (!this.activeOffer) return;

      this.activeOffer.hidden = false;
      this.moveAddedLine(this.activeOffer);
      this.track("upsell_offer_viewed", this.activeOffer);

      this.activeOffer
        .querySelector("[data-accept]")
        .addEventListener("click", (e) => this.accept(e.currentTarget));
      this.activeOffer
        .querySelector("[data-decline]")
        .addEventListener("click", () => this.decline());

      if (state.animate) {
        WDN.UpsellOfferState.update(function (s) {
          s.animate = false;
        });
        // Let the drawer slide in first, then unfold the step.
        setTimeout(() => this.classList.add("is-open"), 250);
      } else {
        this.classList.add("is-open", "is-instant");
      }
    }
  }

  customElements.define("wdn-upsell-offer-step", WDNUpsellOfferStep);
}
